import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useTheme } from '@/lib/hooks'
import NumericKeypad from '@/components/NumericKeypad/NumericKeypad'

import { addToCart } from '../../store/checkoutSlice'
import CashTab from './CashTab'
import LastScannedItem from './LastScannedItem'
import ProductGrid from './ProductGrid'
import ScannerInput from './ScannerInput'
import TransactionsTab from './TransactionsTab'
import {
  CategoriesRow,
  CategoryChipInline,
  KeypadColumn,
  LeftPanelWrapper,
  ScrollContent,
  Tab,
  TabBar,
  TabContent,
  TabIcon,
  TopRow,
  TopRowAside,
  TopRowMain,
  WorkArea,
} from './LeftPanel.styles'

const LeftPanel = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const themes = useTheme()
  const lastScanned = useSelector(state => state.checkout.lastScannedProduct)
  const [activeTab, setActiveTab] = useState('pos')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState('All')

  const categories = useMemo(() => {
    const cats = [...new Set(themes.products.map(p => p.category).filter(Boolean))]
    return ['All', ...cats.sort()]
  }, [themes.products])

  const handleScan = useCallback((query) => {
    console.log('[LEFT_PANEL] handleScan called with:', query)
    const q = query.toLowerCase()
    const product = themes.products.find(
      p => p.barcode === query || p.id === query ||
           p.id?.toLowerCase() === q ||
           p.title?.toLowerCase().includes(q) ||
           p.titlePl?.toLowerCase().includes(q)
    )
    if (product) {
      console.log('[LEFT_PANEL] Scan matched product:', product.title, product.id)
      dispatch(addToCart(product))
    } else {
      console.log('[LEFT_PANEL] Scan no match for:', query)
    }
    setSearchQuery('')
  }, [dispatch, themes.products])

  const handleProductClick = useCallback((product) => {
    console.log('[LEFT_PANEL] Product clicked:', product.title, product.id)
    dispatch(addToCart(product))
  }, [dispatch])

  const handleKeypadSubmit = () => {
    if (searchQuery.trim()) {
      handleScan(searchQuery.trim())
    }
  }

  return (
    <LeftPanelWrapper>
      <TabBar>
        <Tab active={activeTab === 'pos'} onClick={() => setActiveTab('pos')}>
          <TabIcon>{t('tabs.pos')}</TabIcon>
        </Tab>
        <Tab active={activeTab === 'transactions'} onClick={() => setActiveTab('transactions')}>
          <TabIcon>{t('tabs.transactions')}</TabIcon>
        </Tab>
        <Tab active={activeTab === 'cash'} onClick={() => setActiveTab('cash')}>
          <TabIcon>{t('tabs.cash')}</TabIcon>
        </Tab>
      </TabBar>

      <TabContent>
        {activeTab === 'pos' && (
          <>
            <TopRow>
              <TopRowMain>
                <ScannerInput onScan={handleScan} value={searchQuery} onChange={setSearchQuery} />
              </TopRowMain>
              <TopRowAside>
                <LastScannedItem product={lastScanned} currency={themes.currency} />
              </TopRowAside>
            </TopRow>

            <CategoriesRow>
              {categories.map(cat => (
                <CategoryChipInline
                  key={cat}
                  active={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                >
                  {t(`categories.${cat}`, { defaultValue: cat })}
                </CategoryChipInline>
              ))}
            </CategoriesRow>

            <WorkArea>
              <KeypadColumn>
                <NumericKeypad
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSubmit={handleKeypadSubmit}
                  square
                />
              </KeypadColumn>

              <ProductGrid
                products={themes.products}
                currency={themes.currency}
                onProductClick={handleProductClick}
                filterQuery={searchQuery}
                activeCategory={activeCategory}
              />
            </WorkArea>
          </>
        )}
        {activeTab === 'transactions' && <ScrollContent><TransactionsTab /></ScrollContent>}
        {activeTab === 'cash' && <ScrollContent><CashTab /></ScrollContent>}
      </TabContent>
    </LeftPanelWrapper>
  )
}

export default LeftPanel
